import React, { useState } from 'react';
import { useImportCsvMutation } from '../../../services/importApi';
import { Layout } from '../../components/Layout';
import { ToolBar } from '../../components/ToolBar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { toast } from 'react-toastify';
import CircularProgress from '@mui/material/CircularProgress'; // Import CircularProgress
import Box from '@mui/material/Box';

export function ImportPage(props) {
  const [file, setFile] = useState<File | null>(null);
  const [importCsv, importResult] = useImportCsvMutation();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState('');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleUploadClick = () => {
    if (file) {
      setConfirmOpen(true);
    } else {
      toast.error('ファイルを選択してください', {
        position: 'bottom-center',
        autoClose: 3000,
      });
    }
  };

  const handleConfirmUpload = async () => {
    setConfirmOpen(false);
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('filename', file.name);

      // Call the mutation and await the result
      const result = await importCsv(formData).unwrap();

      // Clear file selection on success
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      toast.success(
        'ポイント付与が完了しました。付与履歴から結果が確認できます。',
        {
          position: 'bottom-center',
          autoClose: 5000,
        },
      );
    } catch (error: any) {
      // Handle error cases (non-200 responses)
      console.error('Import failed:', error);

      const errorData = error?.data;
      const errorDetail =
        typeof errorData === 'string'
          ? errorData
          : errorData?.error || errorData?.message || error?.message;
      const csvErrorInfo =
        errorData && typeof errorData === 'object'
          ? [
              errorData.row ? `行番号: ${errorData.row}` : null,
              errorData.value ? `値: ${errorData.value}` : null,
              errorData.error ? `エラー内容: ${errorData.error}` : null,
            ]
              .filter(Boolean)
              .join('\n')
          : errorDetail;

      let errorMessage =
        error?.status === 500 && csvErrorInfo
          ? `アップロードに失敗しました。CSVファイルに問題があります。\n${csvErrorInfo}`
          : 'アップロードに失敗しました。ポイントは付与されていません。同じファイル名のCSVファイルを使用していませんか？重複するファイルは処理できません。';

      // Check if error has status information
      if (error?.status) {
        errorMessage += `\nエラーコード: ${error.status}`;
      }

      // Check if there's a custom error message from the server
      if (!(error?.status === 500 && csvErrorInfo)) {
        if (errorData?.message) {
          errorMessage += `\nエラー詳細: ${errorData.message}`;
        } else if (errorDetail) {
          errorMessage += `\nエラー詳細: ${errorDetail}`;
        }
      }

      setErrorDialogMessage(errorMessage);
    }
  };

  return (
    <Layout title="ポイント付与">
      <ToolBar
        title="ポイント付与"
        noSearchPanel={true}
        noAddButton={true}
        noFilterPanel={true}
      />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <input type="file" onChange={handleFileChange} ref={fileInputRef} />
      <button onClick={handleUploadClick} disabled={importResult.isLoading}>
        {importResult.isLoading
          ? 'アップロード中...'
          : 'CSVファイルをアップロード'}
      </button>

      {/* Loading Overlay */}
      {importResult.isLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              backgroundColor: 'white',
              padding: 3,
              borderRadius: 2,
              textAlign: 'center',
            }}
          >
            <CircularProgress />
            <p style={{ marginTop: '16px' }}>ポイント付与処理中...</p>
          </Box>
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">確認</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            ユーザーさんがポイント付与されます！本当にこのファイルをアップロードしますか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="primary">
            キャンセル
          </Button>
          <Button onClick={handleConfirmUpload} color="primary" autoFocus>
            アップロード
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Dialog */}
      <Dialog
        open={Boolean(errorDialogMessage)}
        onClose={() => setErrorDialogMessage('')}
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
        fullWidth
        maxWidth="md"
      >
        <DialogTitle id="error-dialog-title">アップロード失敗</DialogTitle>
        <DialogContent dividers>
          <DialogContentText
            id="error-dialog-description"
            component="pre"
            sx={{
              margin: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: '60vh',
              overflow: 'auto',
              fontFamily: 'inherit',
              fontSize: '1rem',
              lineHeight: 1.7,
            }}
          >
            {errorDialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialogMessage('')} color="primary">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default ImportPage;
